import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Line } from 'react-chartjs-2';

import { IChartDataSet } from '@dinnisoft/esteio-gestao-common';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
  TooltipItem,
} from 'chart.js';

import { ChartWrapper } from './styles';

ChartJS.register(LineElement, PointElement, LinearScale, Title, CategoryScale, Tooltip, Legend);

interface ICurvaLactacaoChartProps {
  lactacoesDataChart: IChartDataSet[];
}

export interface CurvaLactacaoChartRef {
  getChartImageURI: () => string;
  getChartDimensions: () => { width: number; height: number };
}

const getColor = (index: number): string => {
  const colors = [
    '#0071bc',
    '#4CAF50',
    '#7868c2',
    '#D4A350',
    '#F6771F',
    '#FF4F6D',
    '#9b61bc',
    '#b3496c',
    '#9999CC',
    '#00d5a9',
  ];
  return colors[index % colors.length];
};

const CurvaLactacaoChart = forwardRef<CurvaLactacaoChartRef, ICurvaLactacaoChartProps>(
  ({ lactacoesDataChart }, ref) => {
    const chartRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
      getChartImageURI: () => {
        if (chartRef.current) {
          return chartRef.current.toBase64Image();
        }
        return '';
      },
      getChartDimensions: () => {
        if (chartRef.current) {
          const canvas = chartRef.current.canvas;
          return { width: canvas.width, height: canvas.height };
        }
        return { width: 0, height: 0 };
      },
    }));

    const datasets = lactacoesDataChart.map((dataset, index) => ({
      label: dataset.legenda,
      data: dataset.data.map((point) => ({
        x: Number(point.x), // convert x to a number
        y: Number(point.y),
      })),
      fill: false,
      borderColor: getColor(index),
      backgroundColor: getColor(index),
      tension: 0.4, // Curvas suaves
      showLine: dataset.data.length > 1, // Exibir linha apenas se houver mais de um ponto
      pointRadius: dataset.data.length === 1 ? 5 : 3, // Ponto maior para único ponto
      pointHoverRadius: 6, // Aumenta o tamanho do ponto ao passar o mouse
    }));

    const data: ChartData<'line'> = {
      datasets,
    };

    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false, // Permite altura personalizada
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: 'Curva de Lactação',
        },
        tooltip: {
          mode: 'nearest',
          intersect: false,
          position: 'nearest',
          callbacks: {
            label: (tooltipItem: TooltipItem<'line'>) => {
              const datasetLabel = tooltipItem.dataset.label || '';
              const value = tooltipItem.parsed.y;
              return `${datasetLabel}: ${value} Litros`;
            },
            title: (tooltipItems: TooltipItem<'line'>[]) => {
              const title = tooltipItems[0].parsed.x;
              return `${title} dias`;
            },
          },
          backgroundColor: 'rgba(0, 0, 0, 0.7)', // Personaliza o fundo do tooltip
          titleFont: { size: 14, weight: 'bold' }, // Personaliza a fonte do título do tooltip
          bodyFont: { size: 12 }, // Personaliza a fonte do corpo do tooltip
        },
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false,
      },
      scales: {
        x: {
          type: 'linear',
          position: 'bottom',
          title: {
            display: true,
            text: 'Dias',
          },
          ticks: {
            stepSize: 50,
            callback: (tickValue: string | number) => `${tickValue} dias`,
          },
        },
        y: {
          title: {
            display: true,
            text: 'Litros',
          },
          beginAtZero: true,
          ticks: {
            callback: (tickValue: string | number) => `${tickValue} Litros`,
          },
        },
      },
      hover: {
        mode: 'nearest',
        intersect: false,
      },
    };

    return (
      <ChartWrapper>
        <Line ref={chartRef} data={data} options={options} />
      </ChartWrapper>
    );
  },
);

// Defina o displayName para melhorar a identificação do componente nas ferramentas de desenvolvimento
CurvaLactacaoChart.displayName = 'CurvaLactacaoChart';

export default CurvaLactacaoChart;
